// The platform's toasts (spec §3): modules call toast.show / toast.error and never
// mount anything; the shell mounts the one ToastRegion. No React and no React Aria here,
// so the shell can import this file without growing its main bundle.
//
//   toast.show({ title, message, tone: 'danger' })  → id
//   toast.error(e, 'Save failed')                    the measured dominant pattern
//   toast.close(id)
import { TONES } from './toast-classes.js';
import { devWarn } from '../dev-warn.js';
import { messageOf } from './message-of.js';
import {
  addToast, closeToast, emptyToasts, expireToasts, nextDeadline, pauseToasts, resumeToasts, setTabHidden,
} from './toast-queue.js';

let state = emptyToasts();
let seq = 0;
let now = () => Date.now();
const listeners = new Set();

function set(next) {
  if (next === state) return;
  state = next;
  for (const fn of listeners) fn();
}

function show({ title, message, tone = 'neutral', icon, autoClose } = {}) {
  if ((message === undefined || message === null || message === '') && !title) {
    throw new Error('@platform/ui: toast.show needs a message or a title');
  }
  // A typo'd tone falls back to neutral rather than throwing: toasts are mostly called
  // from .catch handlers, where a throw would swallow the very error being reported.
  let safeTone = tone;
  if (!TONES.includes(tone)) {
    devWarn(`unknown toast tone '${tone}' (one of ${TONES.join(', ')}); showing it as neutral`);
    safeTone = 'neutral';
  }
  seq += 1;
  const id = `toast-${seq}`;
  set(addToast(state, { id, title, message, tone: safeTone, icon, autoClose }, now()));
  return id;
}

export const toast = Object.freeze({
  show,
  close(id) { set(closeToast(state, id, now())); },
  error(err, title = 'Something went wrong') {
    return show({ tone: 'danger', title, message: messageOf(err) });
  },
});

// For ToastRegion only.
export const toastStore = Object.freeze({
  subscribe(fn) {
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  },
  snapshot: () => state,
  pause: () => set(pauseToasts(state, now())),
  resume: () => set(resumeToasts(state, now())),
  setTabHidden: (hidden) => set(setTabHidden(state, hidden, now())),
  expire: () => set(expireToasts(state, now())),
  nextDeadline: () => nextDeadline(state, now()),
});

export function _resetToastsForTests({ now: clock = () => Date.now() } = {}) {
  state = emptyToasts();
  now = clock;
  for (const fn of listeners) fn();
}
